import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import LoginMobileHero from "../components/LoginMobileHero";

const PasswordUpdate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const isResetFlow = location.pathname === "/reset-password";

  const tokenFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("token") || "").trim();
  }, [location.search]);

  const [token, setToken] = useState("");
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [showTempPassword, setShowTempPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const passwordRules = {
    minLength: newPassword.length >= 8,
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecial: /[^A-Za-z0-9]/.test(newPassword),
  };

  const allRulesPassed =
    passwordRules.minLength &&
    passwordRules.hasUpper &&
    passwordRules.hasLower &&
    passwordRules.hasNumber &&
    passwordRules.hasSpecial;

  // Show password rules only when focused and not all rules are passed
  const showPasswordRules = isNewPasswordFocused && !allRulesPassed;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }

    if (!allRulesPassed) {
      toast.error('Please meet all password conditions before submitting.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      return;
    }

    if (isResetFlow) {
      if (!tokenFromUrl) {
        toast.error("Invalid or missing reset link. Use the link from your email or request a new one.");
        return;
      }
    } else {
      const registrationToken = (tokenFromUrl || token).trim();
      if (!registrationToken) {
        toast.error("Invitation token is required");
        return;
      }
      if (!tempPassword?.trim()) {
        toast.error("Temporary password is required");
        return;
      }
    }

    setSubmitting(true);
    try {
      let payload;
      if (isResetFlow) {
        payload = {
          action: "resetPassword",
          token: tokenFromUrl,
          newPassword,
        };
      } else {
        const registrationToken = (tokenFromUrl || token).trim();
        payload = {
          action: "completeRegistration",
          token: registrationToken,
          temporaryPassword: tempPassword,
          newPassword,
        };
      }

      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success") {
        toast.success(
          data?.message ||
            (isResetFlow ? "Password updated successfully" : "Registration completed successfully")
        );
        navigate("/", { replace: true });
      } else {
        toast.error(data?.message || "Password update failed");
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderRule = (passed, text) => (
    <li className={`password-rule-item ${passed ? 'passed' : ''}`}>
      <span className="password-rule-icon">{passed ? '✔' : '●'}</span>
      <span className="password-rule-text">{text}</span>
    </li>
  );

  return (
    <div className="login-container">
      <div className="login-image-section mobile-image">
        <LoginMobileHero />
      </div>

      {/* Left Panel - Password Update Form */}
      <div className="login-left-panel">
        <div className="login-content">
          {/* Header Section */}
          <div className="login-header">
            <img src="/Assets/Images/Adukkumadi-Logos.png" alt="Logo" className="login-logo-image" />
          </div>

          {/* Password Update Form Section */}
          <div className="login-form-section">
            <h1 className="login-title">{isResetFlow ? "Reset password" : "Update Password"}</h1>
            {isResetFlow && !tokenFromUrl ? (
              <p className="text-danger" style={{ fontSize: 14, marginBottom: 12 }}>
                This reset link is missing a token. Open the link from your email or use Forgot password to get a new one.
              </p>
            ) : null}

            <form onSubmit={handleSubmit} className="login-form">
              {!isResetFlow && !tokenFromUrl && (
                <div className="input-wrapper">
                  <input
                    type="text"
                    placeholder="Invitation token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    className="login-input"
                    required
                  />
                </div>
              )}

              {!isResetFlow && (
                <div className="input-wrapper password-input-only">
                  <input
                    type={showTempPassword ? "text" : "password"}
                    placeholder="Temporary password"
                    value={tempPassword}
                    onChange={(e) => setTempPassword(e.target.value)}
                    className="login-input"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowTempPassword(!showTempPassword)}
                  >
                    {showTempPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 16L16 4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </div>
              )}

              {/* New Password */}
              <div className="input-wrapper password-input-only">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="New password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onFocus={() => setIsNewPasswordFocused(true)}
                  onBlur={() => setIsNewPasswordFocused(false)}
                  className="login-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 16L16 4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Password Rules - Show only when focused and not all rules passed */}
              {showPasswordRules && (
                <ul className="password-rules">
                  {renderRule(passwordRules.minLength, 'At least 8 characters')}
                  {renderRule(passwordRules.hasUpper, 'At least one uppercase letter')}
                  {renderRule(passwordRules.hasLower, 'At least one lowercase letter')}
                  {renderRule(passwordRules.hasNumber, 'At least one number')}
                  {renderRule(passwordRules.hasSpecial, 'At least one special character')}
                </ul>
              )}

              {/* Confirm Password */}
              <div className="input-wrapper password-input-only">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="login-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10C2.5 10 5.83333 4.16667 10 4.16667C14.1667 4.16667 17.5 10 17.5 10C17.5 10 14.1667 15.8333 10 15.8333C5.83333 15.8333 2.5 10 2.5 10Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4 16L16 4" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-next-btn"
                disabled={submitting || (isResetFlow && !tokenFromUrl)}
              >
                {submitting ? "Updating..." : "Update Password"}
              </button>
            </form>

            {/* Back to Login / Forgot Link */}
            
          </div>
        </div>
      </div>

      {/* Right Panel - Image (Desktop) */}
      <div className="login-right-panel desktop-image">
        <div className="image-wrapper">
          <img src="/Assets/Images/loginpage.png" alt="Adukkumadi" className="login-image" />
        </div>
      </div>

      {/* Footer - Centered across entire page */}
      <div className="login-footer">
        <p className="login-footer-text">
          Developed by{' '}
          <a href="https://instatenders.in" target="_blank" rel="noopener noreferrer" className="footer-link">
            instatenders.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default PasswordUpdate;